import {
  File,
  HlReel,
  ClipSelection,
  HlReelSchemaBlock,
  HlReelSchemaSelects,
  CreationPreviewClip,
} from '../../../../types';
import { isEmpty, msecToSec, getFileUrl } from '../../../../utils';

interface Props {
  hlReel: HlReel;
  clips: File[];
  clipSelection?: ClipSelection;
}

export const getHlReelPreviewData = (props: Props): HlReelSchemaBlock[] => {
  const { hlReel, clipSelection, clips } = props;

  const hlReelSchemaBlocks: HlReelSchemaBlock[] = [];

  let i = 0;

  hlReel.shots.forEach((shot) => {
    let startTime = msecToSec(shot.segment.startTimestampMilliseconds) ?? 0;
    const endTime = msecToSec(shot.segment.endTimestampMilliseconds - 33);
    const duration = msecToSec(shot.segment.durationMilliseconds);

    // all shots between wildcards to combine in 1 block
    if (shot.type === 'shot') {
      // if block i doesn't exist – create one with first shot data
      if (!hlReelSchemaBlocks[i]) {
        hlReelSchemaBlocks.push({
          type: 'selects',
          startTime,
          duration: endTime - startTime,
          frameIds: [shot.frameId],
        });
      }

      // if exists – update endTime of the block
      else {
        const selectBlock = hlReelSchemaBlocks[i] as HlReelSchemaSelects;
        hlReelSchemaBlocks[i] = {
          ...selectBlock,
          duration: endTime - selectBlock.startTime,
          frameIds: [...(selectBlock.frameIds ?? []), shot.frameId],
        };
      }
    }

    // each placeholder/wildcard is a separate block
    else {
      if (!isEmpty(hlReelSchemaBlocks)) ++i;

      let clipId: string | null = null;
      let frameId = null;
      let elementId = null;
      let name = null;
      let isFilled = false;

      if (clipSelection) {
        clipId =
          clipSelection.wildcardsFiller.find(
            (clip) => clip.wildCardShotIndex === shot.shotIndex,
          )?.clipId ?? null;

        const foundClip = clips.find((clip) => clip.elementId === clipId);

        if (foundClip) {
          frameId = foundClip.frameId ?? null;
          elementId = foundClip.parentElementId ?? null;

          const fileNameParts = foundClip.name?.split('@') ?? [];
          name = fileNameParts[0] + '@' + fileNameParts[1];

          startTime = msecToSec(foundClip.segment?.startTimestampMilliseconds);

          isFilled = true;
        }
      }

      hlReelSchemaBlocks.push({
        type: 'wildcard',
        startTime,
        duration,
        shotIndex: shot.shotIndex,
        clipId,
        frameId,
        elementId,
        name,
        isFilled,
      });

      ++i;
    }
  });

  return hlReelSchemaBlocks;
};

export const getCreationPreviewClips = (props: {
  hlReel: HlReel;
  selectedClips: File[];
  clipSelection: ClipSelection;
}): CreationPreviewClip[] => {
  const { hlReel, clipSelection, selectedClips } = props;

  const previewClips = hlReel.shots
    .filter((shot) => shot.type === 'placeholder')
    .map((shot) => {
      const reelStart = msecToSec(shot.segment.startTimestampMilliseconds - 99);
      const reelEnd = msecToSec(shot.segment.endTimestampMilliseconds);
      let clipStart = 0;
      let elementId = '';
      let src = '';

      const clipId =
        clipSelection.wildcardsFiller.find(
          (clip) => clip.wildCardShotIndex === shot.shotIndex,
        )?.clipId ?? null;

      const foundClip = selectedClips.find((clip) => clip.elementId === clipId);

      if (foundClip) {
        elementId = foundClip.parentElementId ?? '';

        if (foundClip.segment)
          clipStart = msecToSec(
            foundClip.segment.startTimestampMilliseconds + 33,
          );

        const fileNameParts = foundClip.name?.split('@') ?? [];
        const name = fileNameParts[0] + '@' + fileNameParts[1];

        src = getFileUrl(elementId, name, 'mp4');
      }

      return {
        shotIndex: shot.shotIndex,
        reelStart,
        reelEnd,
        clipStart,
        src,
      };
    });

  return previewClips;
};
